import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import useChosenRules from '../hooks/useChosenRules';
import useFilterQueryValues from '../hooks/useFilterQueryValues';
import { BulkDuplicatePayload, useBulkDuplicate, useCreateFlow, useRulesFlowMetrics, useRulesList } from '../api';
import { isEmpty, omit } from 'lodash-es';
import { NotificationChannel, Rule, RuleStatusValue } from '../types';
import { ModalProps, Option } from 'types/shared';
import { FC, Suspense, SyntheticEvent, useCallback, useEffect } from 'react';
import { FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import bulkDuplicateSchema from '../validation/bulkDuplicate.schema';
import { Add, ArrowForward, CachedOutlined, InfoOutlined } from '@mui/icons-material';
import { RuleStatusParameters } from './RulesComponents';
import Select from 'components/common/Select';
import { DiscardChangesDialog, useNotificationChannels, useSyncPlatforms } from '..';
import { ProjectSelect } from 'features/projects';
import ChipInput from 'components/common/ChipInput';
import Input from 'components/common/Input';
import { observer } from 'mobx-react-lite';
import bulkDuplicateStore from '../stores/bulkDuplicateStore';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import { useModal } from 'hooks';
import { FlowsModify } from './FlowsModify';
import { flowsModify } from '../validation/flowsModify.schema';
import RuleUIFormActionBlock from './RuleUIFormActionBlock';
import { queryClient } from 'lib/react-query';
import { toJS } from 'mobx';
import { Navigate, useNavigate } from 'react-router';
import { createSearchParams } from 'react-router-dom';
import useListParams from 'hooks/useListParams';

interface FormData {
  name: string | undefined;
  replaceWith: string | undefined;
  project: number | null;
  flow: string;
  description: string | null;
  notificationChannels: string;
  facebookAdAccountIds: any[];
  status: RuleStatusValue;
}

const SuspenseFallbackSkeleton = () => (
  <Box sx={{ display: 'flex', flex: 1, height: 40, flexDirection: 'column', gap: 1.5 }}>
    <Skeleton
      width={90}
      height={18}
      sx={(t) => ({
        transform: 'translateY(0)',
        m: 0,
        borderRadius: t.borderRadius.md,
      })}
    />
    <Skeleton
      width={'100%'}
      height={40}
      sx={(t) => ({
        transform: 'translateY(0)',
        m: 0,
        borderRadius: t.borderRadius.md,
      })}
    />
  </Box>
);

export const AddFlowForm: FC<{ close: () => void; project: string }> = ({ close, project }) => {
  const { onShowAlert } = useEnqueueSnackbar();

  const formMethods = useForm({
    resolver: yupResolver(flowsModify),
    defaultValues: {
      name: '',
    },
  });

  const { reset, setError } = formMethods;

  const createFlow = useCreateFlow({
    config: {
      onSuccess: () => {
        onShowAlert('New flow created');
        reset();
        close();
      },
      onError: (err) => {
        setError('name', { message: err.cause?.message }, { shouldFocus: true });
      },
    },
  });

  const onSubmit = useCallback(
    ({ name }: { name: string }) => {
      createFlow.mutate({
        project_id: parseInt(project as string),
        name,
      });
    },
    [createFlow, project]
  );

  return (
    <FormProvider {...formMethods}>
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Input name="name" label="Flow name" required />
        </Box>
        <Button
          startIcon={createFlow.isPending ? <CircularProgress size={16} color="inherit" /> : null}
          variant="contained"
          onClick={formMethods.handleSubmit(onSubmit)}
          disabled={createFlow.isPending}
        >
          Create
        </Button>
      </Box>
    </FormProvider>
  );
};

const FlowsAutocomplete = () => {
  const { open, close, isOpen } = useModal();
  const {
    trigger,
    formState: { errors },
  } = useFormContext();
  const projectId = useWatch({ name: 'project' });

  const { data: flowsList, isLoading } = useSyncPlatforms<Option[]>({
    params: {
      project_id: projectId,
    },
    config: {
      enabled: !!projectId,
      select: (data): Option[] => {
        return data.platforms.flatMap(({ list }) =>
          list.filter((i) => `${i.id}` !== 'not_assigned').map(({ id, name }) => ({ value: id, label: name }))
        );
      },
    },
  });

  const handleAddFlow = useCallback(() => {
    open();
  }, [open]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {!isLoading ? (
        <>
          <Select
            name="flow"
            label="Flow"
            required
            selectAll={false}
            options={flowsList ?? []}
            disabled={!projectId}
            onBlur={() => trigger('flow')}
            footerElement={
              <Box
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  left: 0,
                  width: '100%',
                  backgroundColor: 'white',
                  display: 'flex',
                  justifyContent: 'center',
                  py: 1,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Button startIcon={<Add />} variant="text" size="small" color="primary" onClick={handleAddFlow}>
                  Add flow
                </Button>
              </Box>
            }
          />
          <FlowsModify isOpen={isOpen} close={close} modalProps={{ sx: { zIndex: 1400 } }}>
            <AddFlowForm close={close} project={projectId} />
          </FlowsModify>
        </>
      ) : (
        <SuspenseFallbackSkeleton />
      )}
    </Box>
  );
};

const AddPostfix = () => {
  const { onShowAlert, onShowErrorAlert } = useEnqueueSnackbar();
  const handleAddPostfix = useCallback(async () => {
    try {
      const r = await bulkDuplicateStore.addPostfix();

      onShowAlert(r.message, { delayAlert: 3500 });
    } catch (err) {
      onShowErrorAlert((err as { message: string }).message, { delayAlert: 3500 });
    }
  }, [bulkDuplicateStore]);

  return (
    <Box
      sx={{
        position: 'absolute',
        right: 0,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'end',
        alignItems: 'center',
        maxHeight: 24,
      }}
    >
      <Button variant="text" size="small" onClick={handleAddPostfix}>
        Add postfix
      </Button>
      <Typography variant="body2" sx={(t) => ({ color: t.palette.text.secondary })}>{`"duplicated_{date}"`}</Typography>
    </Box>
  );
};

const ReplaceName = observer(() => {
  const isRenamingApplied = bulkDuplicateStore.isRenamingApplied;
  const nameField = useWatch({ name: 'name' });
  const replaceWithField = useWatch({ name: 'replaceWith' });
  const {
    resetField,
    formState: { errors },
  } = useFormContext();
  const { onShowAlert, onShowErrorAlert } = useEnqueueSnackbar();

  const handleResetForm = useCallback(() => {
    resetField('name');
    resetField('replaceWith');
  }, [resetField]);

  const handleRenameRules = useCallback(
    async (e: SyntheticEvent) => {
      e.stopPropagation();
      try {
        const r = await bulkDuplicateStore.renameRules(nameField, replaceWithField);
        onShowAlert(r.message, { delayAlert: 3500 });
        handleResetForm();
      } catch (err) {
        onShowErrorAlert((err as { message: string }).message, { delayAlert: 3500 });
      }
    },
    [nameField, replaceWithField, bulkDuplicateStore, onShowAlert, handleResetForm]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', position: 'relative' }}>
      <AddPostfix />
      <Input
        name="name"
        label={isRenamingApplied ? 'Name' : 'Name*'}
        placeholder="Find"
        helperText="Type part of the name that has to be replaced in each rule"
      />
      <Input name="replaceWith" placeholder="Replace with" containerSx={{ width: '100%' }} />
      <Box>
        <Button
          disabled={!nameField?.length || !replaceWithField?.length}
          startIcon={<CachedOutlined />}
          variant="text"
          size="small"
          onClick={handleRenameRules}
        >
          Rename and update
        </Button>
      </Box>
    </Box>
  );
});

interface NotificationChannelsSelectInputProps {
  label?: string;
  inputLabel?: string;
  name: string;
  required?: boolean;
}

export const NotificationChannelsSelect: FC<NotificationChannelsSelectInputProps> = ({
  name,
  label,
  inputLabel,
  required,
}) => {
  const { trigger } = useFormContext();
  const { data: notificationChannels } = useNotificationChannels<Option[]>({
    config: {
      select: (data): Option[] => {
        return data.map(({ id, name }) => ({ value: id, label: name }));
      },
    },
  });

  return (
    <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
      {label && !inputLabel && (
        <Typography variant="subtitle2" mb={1}>
          {label}
          {required && '*'}
        </Typography>
      )}
      <FormControl sx={{ flex: 1 }}>
        <Select
          name={name}
          id={name}
          label={inputLabel}
          required
          selectAll={false}
          options={notificationChannels ?? []}
          onBlur={() => trigger(name)}
        />
      </FormControl>
    </Box>
  );
};

const FlowMetrics: FC = observer(() => {
  const flowId = useWatch({ name: 'flow' });
  const rulesIds = toJS(bulkDuplicateStore.list).map((r) => r.id);

  const { data, isLoading } = useRulesFlowMetrics({
    params: {
      flow_id: Number(flowId),
      rules_ids: rulesIds ?? [],
    },
    config: {
      enabled: !!flowId,
    },
  });

  const rulesFlowMetrics = data?.data ?? [];

  if (!flowId)
    return (
      <Box sx={{ display: 'flex', gap: 1, width: '100%', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle2">Flow metrics</Typography>
          <Typography
            variant="caption"
            sx={(t) => ({ display: 'flex', alignItems: 'center', gap: 1, color: t.palette.text.secondary })}
          >
            Metrics will be updated automatically based on selected flow
          </Typography>
        </Box>
      </Box>
    );

  return isLoading ? (
    <SuspenseFallbackSkeleton />
  ) : rulesFlowMetrics.length ? (
    <Box sx={{ display: 'flex', gap: 1, width: '100%', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle2">Flow metrics</Typography>
        <Typography variant="caption" sx={(t) => ({ color: t.palette.text.secondary })}>
          {data?.metrics_count} {data && data?.metrics_count > 1 ? 'metrics' : 'metric'} from {data?.rules_count}{' '}
          {data && data?.rules_count > 1 ? 'rules' : 'rule'} will be updated based on the selected flow
        </Typography>
      </Box>
      <Box sx={{ flex: 1, display: 'flex', gap: 1, width: '100%', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', flexWrap: 'nowrap' }}>
          <Box sx={{ flex: '0 0 50%' }}>
            <Typography variant="subtitle2">Original rule</Typography>
          </Box>
          <Box sx={{ flex: '0 0 20px' }} />
          <Box sx={{ flex: '0 0 50%' }}>
            <Typography variant="subtitle2">New rule</Typography>
          </Box>
        </Box>
        <Box
          sx={{ display: 'flex', gap: 1, width: '100%', flexDirection: 'column', maxHeight: 400, overflowY: 'auto' }}
        >
          {rulesFlowMetrics.map((r, i) => (
            <Box
              key={`${i}.${r.rule_id}:${r.old_value}.${r.new_value}`}
              sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'space-between', flexWrap: 'nowrap' }}
            >
              <Box sx={{ flex: 1, display: 'flex', gap: 0.5, flexWrap: 'nowrap', height: 36 }}>
                <Box sx={{ flex: '0 0 20px', display: 'flex', alignItems: 'center' }}>
                  <Tooltip title={r.rule_name} placement="left" arrow>
                    <InfoOutlined
                      sx={(t) => ({ fontSize: t.typography.h4.fontSize, color: t.palette.action.active, m: 0 })}
                    />
                  </Tooltip>
                </Box>
                <Box sx={{ position: 'relative', flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={(t) => ({
                      position: 'absolute',
                      color: t.palette.action.disabled,
                      border: `1px solid ${t.palette.divider}`,
                      borderRadius: t.borderRadius.md,
                      padding: 1,
                      display: 'block',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      width: '100%',
                      overflow: 'hidden',
                      pointerEvents: 'none',
                    })}
                  >
                    {r.old_value}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ flex: '0 0 20px', display: 'flex', alignItems: 'center' }}>
                <ArrowForward />
              </Box>
              <Box sx={{ flex: 1, position: 'relative' }}>
                <Typography
                  variant="body2"
                  sx={(t) => ({
                    position: 'absolute',
                    border: `1px solid ${t.palette.divider}`,
                    borderRadius: t.borderRadius.md,
                    padding: 1,
                    display: 'block',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    width: '100%',
                    overflow: 'hidden',
                    pointerEvents: 'none',
                  })}
                >
                  {r.new_value}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  ) : (
    <Box sx={{ display: 'flex', gap: 1, width: '100%', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle2">Flow metrics</Typography>
        <Typography variant="caption" sx={(t) => ({ color: t.palette.text.secondary })}>
          No data received
        </Typography>
      </Box>
    </Box>
  );
});

const HighlightSearchTerm = ({
  originalText,
  searchTerm,
  replaceTerm,
}: {
  originalText: string;
  searchTerm: string;
  replaceTerm: string;
}) => {
  if (!searchTerm)
    return (
      <Typography component="span" variant="body2" sx={{ wordBreak: 'break-all', pr: 2, m: 0, whiteSpace: 'pre-wrap' }}>
        {originalText}
      </Typography>
    );

  // special character must be properly escaped in the regular expression
  const escapedString = searchTerm.replace(/[\W_]/g, '\\$&'); // $& means the whole matched string
  const highlightedText = originalText.replace(new RegExp(escapedString, 'gi'), (match) => {
    return `<span style="background-color: rgba(209, 221, 253, 1); color: rgba(61, 90, 254, 1)">${replaceTerm || match}</span>`;
  });

  return (
    <Typography
      component="span"
      variant="body2"
      sx={{ wordBreak: 'break-all', pr: 2, m: 0, whiteSpace: 'pre-wrap' }}
      dangerouslySetInnerHTML={{ __html: highlightedText }}
    ></Typography>
  );
};

const RulesList = observer(() => {
  const nameField = useWatch({ name: 'name' });
  const replaceWithField = useWatch({ name: 'replaceWith' });
  const rules = bulkDuplicateStore.list;
  const defaultStateList = bulkDuplicateStore.defaultStateList;

  const lastRenamedRules = bulkDuplicateStore.lastRenamedRules;

  const handleHighlightState = () => {
    bulkDuplicateStore.clearRenamedRules();
  };

  useEffect(() => {
    const MuiDialog = document.querySelector('.MuiDialog-container');
    MuiDialog?.addEventListener('click', handleHighlightState);
    return () => {
      MuiDialog?.removeEventListener('click', handleHighlightState);
    };
  }, []);

  return rules.map((r) => {
    const isRenamed = defaultStateList.find((i) => i.id === r.id)?.name !== r.name;

    return (
      <TableRow
        key={`DuplicateRulesItem_${r.id}_${r.name}`}
        hover
        sx={(t) => (lastRenamedRules.includes(r.id) ? { backgroundColor: t.palette.action.focus } : {})}
      >
        <TableCell component="td">
          <HighlightSearchTerm originalText={r.name} searchTerm={nameField} replaceTerm={replaceWithField} />
        </TableCell>
        <TableCell component="td">
          {!isRenamed && (
            <Tooltip title="Change name to save" placement="left" arrow>
              <InfoOutlined sx={(t) => ({ color: t.palette.warning.main })} />
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
    );
  });
});

const BulkDuplicateForm: FC<Pick<ModalProps, 'close'>> = observer(({ close }) => {
  const { onShowAlert, onShowErrorAlert } = useEnqueueSnackbar();
  const { project, setProject } = useListParams();
  const { isOpen: isOpenDiscard, open: openDiscard, close: closeDiscard } = useModal();
  const { rulesIds, isAllRulesChosen } = useChosenRules();
  const params = useFilterQueryValues();
  const navigate = useNavigate();

  const { data: rules } = useRulesList<Rule[]>({
    params: omit(params, ['paginationRelatedParams']),
    config: {
      select: ({ data }) => {
        const filteredData = isAllRulesChosen ? data : data.filter(({ id }) => rulesIds.includes(id));
        return filteredData;
      },
    },
  });

  const storeRules = toJS(bulkDuplicateStore.list);
  const isRenamingApplied = bulkDuplicateStore.isRenamingApplied;

  useEffect(() => {
    !isEmpty(rules) && bulkDuplicateStore.initialRules(rules);
  }, [rules]);

  const formMethods = useForm<FormData>({
    defaultValues: {
      name: '',
      replaceWith: '',
      project: params.project_id ?? null,
      flow: '',
      description: null,
      notificationChannels: '',
      facebookAdAccountIds: [],
      status: 'paused',
    },
    // @ts-ignore
    resolver: yupResolver(bulkDuplicateSchema),
    context: { isRenamingApplied },
  });

  const {
    reset,
    clearErrors,
    setValue,
    formState: { errors },
    handleSubmit,
    watch,
  } = formMethods;

  const projectField = watch('project');

  useEffect(() => {
    if (projectField) {
      setValue('flow', '', { shouldValidate: false });
    }
  }, [projectField]);

  useEffect(() => {
    if (isRenamingApplied && !!errors?.name) {
      clearErrors('name');
    }
  }, [isRenamingApplied]);

  const duplicate = useBulkDuplicate({
    config: {
      onSuccess: (_, variables: BulkDuplicatePayload[]) => {
        const { flow_id, project_id, status } = variables[0];
        navigate({
          pathname: '/rules',
          search: createSearchParams({
            listId: `${flow_id}`,
            project: `${project_id}`,
            statusState: `${status}`,
            tab: `${status}`,
            platform: params.platform_id ?? 1,
          } as {}).toString(),
        });
        reset();
        close();
        onShowAlert('Success');
      },
      onError: (err) => {
        onShowErrorAlert(`${(err?.message || err?.cause?.message) ?? 'Error'}`);
      },
    },
  });

  const onDiscard = useCallback(() => {
    reset();
    closeDiscard();
    close();
  }, [reset, close, closeDiscard]);

  const onCloseDiscard = useCallback(() => {
    closeDiscard();
  }, [closeDiscard]);

  const onSubmit = ({
    status,
    flow: flow_id,
    notificationChannels,
    project: project_id,
    facebookAdAccountIds: facebook_ad_account_ids,
  }: FormData) => {
    if (!isRenamingApplied) {
      setValue('name', '', { shouldValidate: true });
      return;
    }

    const cachedNotificationData = queryClient.getQueriesData<NotificationChannel[]>({
      queryKey: ['notification_channels'],
    })[0][1];

    const notificationChanel = cachedNotificationData?.find((n) => n.id === notificationChannels);

    const payload: BulkDuplicatePayload[] = storeRules.map(({ id, name }) => ({
      id,
      name,
      status: status as RuleStatusValue,
      flow_id: parseInt(flow_id),
      project_id: project_id!,
      facebook_ad_account_ids,
      notification_channels: notificationChanel ? [notificationChanel] : [],
    }));

    duplicate.mutate(payload);
  };

  return (
    <Box
      sx={{ display: 'flex', flexGrow: 1, height: '100%', minWidth: 990, overflow: 'auto', flexWrap: 'nowrap', pt: 2 }}
    >
      {rules.length ? (
        <FormProvider {...formMethods}>
          <Box
            sx={(t) => ({
              display: 'flex',
              width: '100%',
              p: 0,
              flex: 1,
              borderRight: `1px solid ${t.palette.divider}`,
            })}
          >
            <TableContainer sx={{ width: '100%', p: 0, flex: 1, pr: 5 }}>
              <Table sx={{ '& th': { px: 0 }, '& td': { py: 0, px: 1 } }} stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell component="th" colSpan={2} sx={{ '&': { py: 0, verticalAlign: 'top' } }}>
                      Rules to be created
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <RulesList />
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              pt: 0,
              pb: 16,
              pr: 8,
              pl: 5,
              flex: 1,
              width: '35%',
              maxWidth: 550,
              overflow: 'hidden',
              overflowY: 'auto',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Parameters to edit
              </Typography>
              <Typography variant="caption" sx={(t) => ({ color: t.palette.text.secondary })}>
                <InfoOutlined
                  sx={(t) => ({
                    fontSize: 18,
                    color: t.palette.indigo.main,
                    mr: 0.5,
                    mb: -0.5,
                  })}
                />
                There cannot be two rules with the same name and conditions. Make changes to save new rules.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
              <RuleStatusParameters name="status" sx={{ flexDirection: 'row', justifyContent: 'space-between' }} />
              <ReplaceName />
              <Suspense fallback={<SuspenseFallbackSkeleton />}>
                <ProjectSelect onlyUsersProjects name="project" label="Project" required filter={(p) => !!p.value} />
              </Suspense>
              <FlowsAutocomplete />
              <ChipInput name="facebookAdAccountIds" label="Account" required />
              <Suspense fallback={<SuspenseFallbackSkeleton />}>
                <NotificationChannelsSelect name="notificationChannels" label="Notification channel" required />
              </Suspense>
              <FlowMetrics />
            </Box>
          </Box>
          <DiscardChangesDialog isOpen={isOpenDiscard} onClose={onCloseDiscard} onDiscard={onDiscard} />
          <RuleUIFormActionBlock
            mode={['update']}
            project={{ id: projectField as number, name: '' }}
            isPending={duplicate.isPending}
            onDiscard={openDiscard}
            hasChanges={true}
            onSave={handleSubmit(onSubmit)}
            submitTitle="Duplicate"
          />
        </FormProvider>
      ) : (
        <Box
          sx={(t) => ({
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
          })}
        >
          <Typography variant="body1" sx={(t) => ({ color: t.palette.text.primary })}>
            There are no rules for duplication
          </Typography>
        </Box>
      )}
    </Box>
  );
});

export default BulkDuplicateForm;
